import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/Select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/Card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/Dialog';
import { Badge } from '../ui/Badge';
import {
    User,
    X,
    CheckCircle,
    AlertCircle,
    Loader2,
    MapPin,
    Phone,
    Mail,
    Shield,
    Truck
} from 'lucide-react';
import { apiService, authService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import DriverRegistrationSuccess from '../fleet/DriverRegistrationSuccess';
import CountryCodeSelector from '../ui/CountryCodeSelector';

const AdminDriverRegistrationModal = ({ onSuccess, onError, children, inline = false, onCancel }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isInitializing, setIsInitializing] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [registeredDriver, setRegisteredDriver] = useState(null);
    const [fleetOperators, setFleetOperators] = useState([]);
    const { user } = useAuth();

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        phone_country_code: '+91',
        email: '',
        password: '',
        license_number: '',
        experience_level: 'BEGINNER',
        operator_id: '',
        current_latitude: '',
        current_longitude: '',
        documents: []
    });

    const [errors, setErrors] = useState({});

    // Fetch fleet operators on component mount
    useEffect(() => {
        const fetchFleetOperators = async () => {
            try {
                setIsInitializing(true);
                // getAllFleetOperators() only returns one page (10 by
                // default) — this walks every page so the dropdown actually
                // shows every operator, not just the first 10.
                const response = await authService.getAllFleetOperatorsUnpaginated();
                console.log('Fleet operators response:', response);

                if (response.success && Array.isArray(response.data)) {
                    setFleetOperators(response.data);
                } else {
                    setFleetOperators([]);
                }
            } catch (error) {
                console.error('Error fetching fleet operators:', error);
                onError?.('Failed to load fleet operators');
            } finally {
                setIsInitializing(false);
            }
        };

        if (isOpen || inline) {
            fetchFleetOperators();
        }
    }, [isOpen, inline]);

    // Stable input change handler
    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    }, [errors]);

    const validateForm = () => {
        const newErrors = {};

        if (!formData.operator_id) {
            newErrors.operator_id = 'Please select a fleet operator';
        }

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^[6-9]\d{9}$/.test(formData.phone.trim())) {
            newErrors.phone = 'Please enter a valid 10-digit Indian mobile number';
        }

        if (!formData.password.trim()) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters long';
        }

        if (!formData.license_number.trim()) {
            newErrors.license_number = 'License number is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }
        setIsLoading(true);

        try {
            if (!formData.operator_id) {
                throw new Error('Operator ID not selected.');
            }

            const driverData = {
                name: formData.name.trim(),
                email: formData.email.trim() || null,
                phone: formData.phone.trim(),
                phone_country_code: formData.phone_country_code,
                password: formData.password.trim(),
                license_number: formData.license_number.trim().toUpperCase(),
                experience_level: formData.experience_level,
                current_latitude: formData.current_latitude ? parseFloat(formData.current_latitude) : null,
                current_longitude: formData.current_longitude ? parseFloat(formData.current_longitude) : null,
                operator_id: formData.operator_id,
                documents: formData.documents
            };

            const result = await apiService.registerDriver(driverData);

            if (result.success) {
                setRegisteredDriver({
                    ...result.data,
                    name: formData.name,
                    phone: formData.phone,
                    email: formData.email,
                    license_number: formData.license_number,
                    experience_level: formData.experience_level,
                    operator_name: fleetOperators.find(op => op.id === formData.operator_id)?.name
                });

                setShowSuccess(true);
                onSuccess?.(result.data);
            } else {
                throw new Error(result.message || 'Registration failed');
            }

        } catch (error) {
            console.error('Registration error:', error);
            onError?.(error.message || 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (inline && onCancel) {
            onCancel();
        } else {
            setIsOpen(false);
        }
        setShowSuccess(false);
        setRegisteredDriver(null);
        setFormData({
            name: '',
            phone: '',
            phone_country_code: '+91',
            email: '',
            password: '',
            license_number: '',
            experience_level: 'BEGINNER',
            operator_id: '',
            current_latitude: '',
            current_longitude: '',
            documents: []
        });
        setErrors({});
    };

    const handleViewDrivers = () => {
        handleClose();
    };

    if (showSuccess && registeredDriver) {
        return (
            <DriverRegistrationSuccess
                driverData={registeredDriver}
                onClose={handleClose}
                onViewDrivers={handleViewDrivers}
            />
        );
    }

    const formContent = (
        <div className="space-y-6">
            {/* Fleet Operator Selection */}
            <Card className="border-blue-100 bg-blue-50/30">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Truck className="w-4 h-4 text-blue-600" />
                        Assign Fleet Operator
                    </CardTitle>
                    <CardDescription>Select the fleet operator this driver will work for</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Fleet Operator *</label>
                        {isInitializing ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Loading operators...
                            </div>
                        ) : (
                            <Select
                                value={formData.operator_id}
                                onValueChange={(value) => handleInputChange('operator_id', value)}
                            >
                                <SelectTrigger className={errors.operator_id ? 'border-red-500' : ''}>
                                    <SelectValue placeholder="Select Fleet Operator" />
                                </SelectTrigger>
                                <SelectContent>
                                    {fleetOperators.map((operator) => (
                                        <SelectItem key={operator.id || operator.operator_id} value={operator.id || operator.operator_id}>
                                            {operator.name} ({operator.city || 'Unknown City'})
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}
                        {errors.operator_id && <p className="text-red-500 text-xs">{errors.operator_id}</p>}
                    </div>
                </CardContent>
            </Card>

            {/* Personal Information */}
            <Card>
                <CardHeader>
                    <CardTitle>Personal Information</CardTitle>
                    <CardDescription>Basic driver information</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="driver-name" className="block text-sm font-medium mb-1">Name *</label>
                            <Input
                                id="driver-name"
                                type="text"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                placeholder="Enter driver name"
                                className={errors.name ? 'border-red-500' : ''}
                            />
                            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                        </div>

                        <div>
                            <label htmlFor="driver-phone" className="block text-sm font-medium mb-1">Phone *</label>
                            <div className="flex gap-2">
                                <div className="w-28">
                                    <CountryCodeSelector
                                        value={formData.phone_country_code}
                                        onChange={(e) => handleInputChange('phone_country_code', e.target.value)}
                                        disabled={isLoading}
                                        placeholder="Country"
                                    />
                                </div>
                                <div className="flex-1">
                                    <Input
                                        id="driver-phone"
                                        type="tel"
                                        value={formData.phone}
                                        onChange={(e) => handleInputChange('phone', e.target.value)}
                                        placeholder="Enter phone number"
                                        className={errors.phone ? 'border-red-500' : ''}
                                    />
                                </div>
                            </div>
                            {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                        </div>

                        <div>
                            <label htmlFor="driver-email" className="block text-sm font-medium mb-1">Email</label>
                            <Input
                                id="driver-email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => handleInputChange('email', e.target.value)}
                                placeholder="Enter email address"
                                className={errors.email ? 'border-red-500' : ''}
                            />
                            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                        </div>

                        <div>
                            <label htmlFor="driver-password" className="block text-sm font-medium mb-1">Password *</label>
                            <Input
                                id="driver-password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                placeholder="Enter password"
                                className={errors.password ? 'border-red-500' : ''}
                            />
                            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Professional Information */}
            <Card>
                <CardHeader>
                    <CardTitle>Professional Information</CardTitle>
                    <CardDescription>Driver license and experience details</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="driver-license" className="block text-sm font-medium mb-1">License Number *</label>
                            <Input
                                id="driver-license"
                                type="text"
                                value={formData.license_number}
                                onChange={(e) => handleInputChange('license_number', e.target.value)}
                                placeholder="Enter license number"
                                className={errors.license_number ? 'border-red-500' : ''}
                            />
                            {errors.license_number && <p className="text-red-500 text-xs mt-1">{errors.license_number}</p>}
                        </div>

                        <div>
                            <label className="block text-sm font-medium mb-1">Experience Level</label>
                            <Select value={formData.experience_level} onValueChange={(value) => handleInputChange('experience_level', value)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select experience level" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BEGINNER">Beginner</SelectItem>
                                    <SelectItem value="INTERMEDIATE">Intermediate</SelectItem>
                                    <SelectItem value="EXPERIENCED">Experienced</SelectItem>
                                    <SelectItem value="EXPERT">Expert</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Location Information */}
            <Card>
                <CardHeader>
                    <CardTitle>Location Information</CardTitle>
                    <CardDescription>Current driver location (optional)</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label htmlFor="driver-lat" className="block text-sm font-medium mb-1">Current Latitude</label>
                            <Input
                                id="driver-lat"
                                type="number"
                                step="any"
                                value={formData.current_latitude}
                                onChange={(e) => handleInputChange('current_latitude', e.target.value)}
                                placeholder="Enter latitude (e.g., 19.0760)"
                                className={errors.current_latitude ? 'border-red-500' : ''}
                            />
                            {errors.current_latitude && <p className="text-red-500 text-xs mt-1">{errors.current_latitude}</p>}
                        </div>

                        <div>
                            <label htmlFor="driver-lng" className="block text-sm font-medium mb-1">Current Longitude</label>
                            <Input
                                id="driver-lng"
                                type="number"
                                step="any"
                                value={formData.current_longitude}
                                onChange={(e) => handleInputChange('current_longitude', e.target.value)}
                                placeholder="Enter longitude (e.g., 72.8777)"
                                className={errors.current_longitude ? 'border-red-500' : ''}
                            />
                            {errors.current_longitude && <p className="text-red-500 text-xs mt-1">{errors.current_longitude}</p>}
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={isLoading}
                    className="min-w-[120px] bg-green-600 hover:bg-green-700 text-white"
                >
                    {isLoading ? (
                        <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Registering...
                        </>
                    ) : (
                        <>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Register Driver
                        </>
                    )}
                </Button>
            </div>
        </div>
    );

    if (inline) {
        return (
            <div className="max-w-6xl">
                <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6 mb-4">
                    <h2 className="text-lg sm:text-xl font-bold mb-2 flex items-center gap-2">
                        <User className="w-5 h-5 text-green-600" />
                        Register New Driver (Admin)
                    </h2>
                    <p className="text-sm text-gray-600">Add a new driver to a fleet. All fields marked with * are required.</p>
                </div>
                <form onSubmit={handleSubmit}>
                    {formContent}
                </form>
            </div>
        );
    }

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
                {children || (
                    <Button className="bg-green-600 hover:bg-green-700 text-white">
                        <User className="w-4 h-4 mr-2" />
                        Add Driver
                    </Button>
                )}
            </DialogTrigger>
            <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto p-0">
                <DialogHeader className="p-6 pb-0">
                    <DialogTitle className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        Register New Driver (Admin)
                    </DialogTitle>
                    <DialogDescription className="pb-4 pl-6">
                        Add a new driver to a specific fleet. All fields marked with * are required.
                    </DialogDescription>
                </DialogHeader>
                <div className="p-6 pt-0 max-h-[80vh] overflow-y-auto">
                    <form onSubmit={handleSubmit}>
                        {formContent}
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AdminDriverRegistrationModal;
